import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  IconButton,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import { ecommerceApi } from '../../config/apiClient';

/**
 * Merkezi Pazaryeri Stok Senkronizasyonu yonetim paneli.
 *
 * - Durum ozeti (kuyruk, son 24s log, aktif eslesme, son senkron)
 * - Otomatik eslestirme + tumunu senkronize et (push-all)
 * - Urun-ilan eslesme tablosu (platform filtresi, aktif/pasif, sil)
 * - Senkron gunlukleri (basarili/basarisiz)
 *
 * gts_ecommerce_api uclarini ecommerceApi (JWT + X-Company-Id) uzerinden cagirir.
 */

interface QueueStat { status: string; count: number; }
interface LogStat { platform: string; success: boolean; count: number; }
interface StatusResponse {
  success: boolean;
  queue: QueueStat[];
  last24hLogs: LogStat[];
  activeMappings: number;
  lastSyncedAt: string | null;
}
interface Mapping {
  id: string;
  inventoryItemId: string;
  platform: string;
  barcode?: string | null;
  stockCode?: string | null;
  merchantSku?: string | null;
  listingId?: string | null;
  isActive: boolean;
  origin: string;
  lastSyncedQuantity?: number | null;
  lastSyncedAt?: string | null;
  lastSyncStatus?: string | null;
  lastSyncMessage?: string | null;
}
interface SyncLog {
  id: string;
  inventoryItemId: string;
  internalStockCode?: string | null;
  platform: string;
  channelKey?: string | null;
  requestedQuantity: number;
  success: boolean;
  reason?: string | null;
  message?: string | null;
  externalBatchId?: string | null;
  createdAt: string;
}

const PLATFORM_LABELS: Record<string, string> = {
  n11: 'N11',
  trendyol: 'Trendyol',
  hepsiburada: 'Hepsiburada',
  website: 'Web Sitesi',
};

const StockSyncPanel: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await ecommerceApi.get<StatusResponse>('/api/ecommerce/stock-sync/status');
      setStatus(data);
    } catch {
      setToast({ msg: 'Durum bilgisi alinamadi.', sev: 'error' });
    }
  }, []);

  const loadMappings = useCallback(async () => {
    try {
      const params = platformFilter ? { platform: platformFilter } : {};
      const { data } = await ecommerceApi.get('/api/ecommerce/product-mappings', { params });
      setMappings(data?.mappings ?? []);
    } catch {
      setToast({ msg: 'Eslesmeler yuklenemedi.', sev: 'error' });
    }
  }, [platformFilter]);

  const loadLogs = useCallback(async () => {
    try {
      const { data } = await ecommerceApi.get('/api/ecommerce/stock-sync/logs', {
        params: { take: 200, onlyFailed },
      });
      setLogs(data?.logs ?? []);
    } catch {
      setToast({ msg: 'Gunlukler yuklenemedi.', sev: 'error' });
    }
  }, [onlyFailed]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadStatus(), loadMappings(), loadLogs()]);
    setLoading(false);
  }, [loadStatus, loadMappings, loadLogs]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadMappings(); }, [platformFilter, loadMappings]);
  useEffect(() => { loadLogs(); }, [onlyFailed, loadLogs]);

  const pendingQueue = status?.queue?.filter((q) => q.status === 'Pending' || q.status === 'Failed' || q.status === 'Processing')
    .reduce((a, b) => a + b.count, 0) ?? 0;
  useEffect(() => {
    if (pendingQueue <= 0) return undefined;
    const id = window.setInterval(() => {
      void Promise.all([loadStatus(), loadMappings(), loadLogs()]);
    }, 8000);
    return () => window.clearInterval(id);
  }, [pendingQueue, loadStatus, loadMappings, loadLogs]);

  const handleAutoMatch = async () => {
    setBusyAction('auto-match');
    try {
      const { data } = await ecommerceApi.post('/api/ecommerce/product-mappings/auto-match');
      const r = data?.result ?? {};
      setToast({
        msg: `Otomatik eslestirme: ${r.created ?? 0} yeni, ${r.updated ?? 0} guncellendi, ${r.unmatched ?? 0} eslesemedi.`,
        sev: 'success',
      });
      await Promise.all([loadMappings(), loadStatus()]);
    } catch {
      setToast({ msg: 'Otomatik eslestirme basarisiz. Aktif pazaryeri entegrasyonu var mi?', sev: 'error' });
    } finally {
      setBusyAction(null);
    }
  };

  const handlePushAll = async () => {
    setBusyAction('push-all');
    try {
      const { data } = await ecommerceApi.post('/api/ecommerce/stock-sync/push-all');
      setToast({ msg: `${data?.enqueued ?? 0} urun senkron kuyruguna alindi.`, sev: 'success' });
      await loadStatus();
    } catch {
      setToast({ msg: 'Toplu senkronizasyon baslatilamadi.', sev: 'error' });
    } finally {
      setBusyAction(null);
    }
  };

  const toggleMappingActive = async (m: Mapping) => {
    try {
      await ecommerceApi.post('/api/ecommerce/product-mappings/upsert', {
        id: m.id,
        inventoryItemId: m.inventoryItemId,
        platform: m.platform,
        barcode: m.barcode,
        stockCode: m.stockCode,
        merchantSku: m.merchantSku,
        listingId: m.listingId,
        isActive: !m.isActive,
      });
      await loadMappings();
    } catch {
      setToast({ msg: 'Eslesme guncellenemedi.', sev: 'error' });
    }
  };

  const deleteMapping = async (m: Mapping) => {
    try {
      await ecommerceApi.post(`/api/ecommerce/product-mappings/delete/${m.id}`);
      await loadMappings();
    } catch {
      setToast({ msg: 'Eslesme silinemedi.', sev: 'error' });
    }
  };

  const pushSingle = async (m: Mapping) => {
    try {
      await ecommerceApi.post(`/api/ecommerce/stock-sync/push-item/${m.inventoryItemId}`);
      setToast({ msg: 'Urun senkron kuyruguna alindi. Worker isleyince gunlukte gorunur.', sev: 'info' });
      await Promise.all([loadStatus(), loadMappings(), loadLogs()]);
    } catch {
      setToast({ msg: 'Kuyruklama basarisiz.', sev: 'error' });
    }
  };

  /** API DateTime.UtcNow'u Z siz donduruyor; yerel saat gibi okunmasin diye UTC kabul et. */
  const fmtDate = (v?: string | null) => {
    if (!v) return '-';
    const hasTz = /Z$|[+-]\d{2}:?\d{2}$/.test(v);
    return new Date(hasTz ? v : `${v}Z`).toLocaleString('tr-TR');
  };

  const failedMappingCount = mappings.filter((m) => m.lastSyncStatus === 'failed').length;
  const failedAttemptCount = status?.last24hLogs?.filter((l) => !l.success).reduce((a, b) => a + b.count, 0) ?? 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Merkezi Stok Senkronizasyonu</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAll}
            disabled={loading}
          >
            Yenile
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={busyAction === 'auto-match' ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
            onClick={handleAutoMatch}
            disabled={busyAction !== null}
          >
            Otomatik Eslestir
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={busyAction === 'push-all' ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handlePushAll}
            disabled={busyAction !== null}
          >
            Tumunu Senkronize Et
          </Button>
        </Box>
      </Box>

      {/* Durum ozeti kartlari */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined"><CardContent>
            <Typography variant="caption" color="text.secondary">Aktif Eslesme</Typography>
            <Typography variant="h5">{status?.activeMappings ?? '-'}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined"><CardContent>
            <Typography variant="caption" color="text.secondary">Kuyrukta Bekleyen</Typography>
            <Typography variant="h5">
              {status?.queue?.filter(q => q.status === 'Pending' || q.status === 'Failed').reduce((a, b) => a + b.count, 0) ?? '-'}
            </Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined"><CardContent>
            <Typography variant="caption" color="text.secondary">Hatali eslesme</Typography>
            <Typography variant="h5" color={failedMappingCount > 0 ? 'error' : 'inherit'}>
              {failedMappingCount}
            </Typography>
            {failedAttemptCount > 0 && (
              <Typography variant="caption" color="text.secondary">{failedAttemptCount} deneme / 24s</Typography>
            )}
          </CardContent></Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card variant="outlined"><CardContent>
            <Typography variant="caption" color="text.secondary">Son Senkron</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>{fmtDate(status?.lastSyncedAt)}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Urun Eslesmeleri" />
          <Tab label="Senkron Gunlukleri" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Platform</InputLabel>
                <Select
                  label="Platform"
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                >
                  <MenuItem value="">Tumu</MenuItem>
                  <MenuItem value="n11">N11</MenuItem>
                  <MenuItem value="trendyol">Trendyol</MenuItem>
                  <MenuItem value="hepsiburada">Hepsiburada</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">{mappings.length} eslesme</Typography>
            </Box>

            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Platform</TableCell>
                    <TableCell>Barkod</TableCell>
                    <TableCell>StockCode</TableCell>
                    <TableCell>MerchantSku</TableCell>
                    <TableCell>Kaynak</TableCell>
                    <TableCell align="right">Son Stok</TableCell>
                    <TableCell>Son Senkron</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell align="right">Islem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((m) => (
                    <TableRow key={m.id} hover>
                      <TableCell><Chip size="small" label={PLATFORM_LABELS[m.platform] ?? m.platform} /></TableCell>
                      <TableCell>{m.barcode || '-'}</TableCell>
                      <TableCell>{m.stockCode || '-'}</TableCell>
                      <TableCell>{m.merchantSku || '-'}</TableCell>
                      <TableCell>{m.origin === 'manual' ? 'Manuel' : 'Otomatik'}</TableCell>
                      <TableCell align="right">{m.lastSyncedQuantity ?? '-'}</TableCell>
                      <TableCell>{fmtDate(m.lastSyncedAt)}</TableCell>
                      <TableCell>
                        <Tooltip title={m.lastSyncMessage || ''}>
                          <Chip
                            size="small"
                            color={m.isActive ? (m.lastSyncStatus === 'failed' ? 'error' : 'success') : 'default'}
                            label={m.isActive ? (m.lastSyncStatus === 'failed' ? 'Hata' : 'Aktif') : 'Pasif'}
                            onClick={() => toggleMappingActive(m)}
                            variant={m.isActive ? 'filled' : 'outlined'}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Simdi senkronize et">
                          <IconButton size="small" onClick={() => pushSingle(m)}><SyncIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Eslesmeyi sil">
                          <IconButton size="small" onClick={() => deleteMapping(m)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {mappings.length === 0 && (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        Eslesme yok. "Otomatik Eslestir" ile e-ticarete acik urunleri aktif pazaryeri kanallarina baglayin.
                      </Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Chip
                label={onlyFailed ? 'Sadece Basarisiz' : 'Tum Gunlukler'}
                color={onlyFailed ? 'error' : 'default'}
                onClick={() => setOnlyFailed(!onlyFailed)}
                variant={onlyFailed ? 'filled' : 'outlined'}
                size="small"
              />
            </Box>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Tarih</TableCell>
                    <TableCell>Platform</TableCell>
                    <TableCell>StockCode</TableCell>
                    <TableCell>Anahtar</TableCell>
                    <TableCell align="right">Miktar</TableCell>
                    <TableCell>Sebep</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>Mesaj</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((l) => (
                    <TableRow key={l.id} hover>
                      <TableCell>{fmtDate(l.createdAt)}</TableCell>
                      <TableCell>{PLATFORM_LABELS[l.platform] ?? l.platform}</TableCell>
                      <TableCell>{l.internalStockCode || '-'}</TableCell>
                      <TableCell>{l.channelKey || '-'}</TableCell>
                      <TableCell align="right">{l.requestedQuantity}</TableCell>
                      <TableCell>{l.reason || '-'}</TableCell>
                      <TableCell>
                        <Chip size="small" color={l.success ? 'success' : 'error'} label={l.success ? 'Basarili' : 'Hata'} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Tooltip title={l.message || ''}><span>{l.message || '-'}</span></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {logs.length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">Gunluk kaydi yok.</Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default StockSyncPanel;
